import { useEffect, useRef } from "react";

/**
 * Two continuous ink lines originating from opposite sides.
 * Left hand: charcoal. Right hand: vermillion. They drift independently,
 * occasionally converging near the vertical middle.
 */
export default function DualLineCanvas({ className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const parent = canvas.parentElement;
      w = parent.clientWidth;
      h = parent.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;
    const left = { x: 0, y: 0, points: [] };
    const right = { x: 0, y: 0, points: [] };
    const MAX = 220;

    const draw = () => {
      t += 0.006;
      // Advance left hand (charcoal) — moves from left edge inward with slow bezier drift
      left.x = 20 + Math.abs(Math.sin(t * 0.9)) * (w * 0.55);
      left.y = h * 0.55 + Math.sin(t * 1.7) * (h * 0.22);
      // Right hand (vermillion) — moves from right inward, independent phase
      right.x = w - 20 - Math.abs(Math.cos(t * 0.7 + 1.3)) * (w * 0.55);
      right.y = h * 0.45 + Math.cos(t * 1.3 + 0.5) * (h * 0.22);

      left.points.push({ x: left.x, y: left.y });
      right.points.push({ x: right.x, y: right.y });
      if (left.points.length > MAX) left.points.shift();
      if (right.points.length > MAX) right.points.shift();

      ctx.clearRect(0, 0, w, h);

      const drawStroke = (pts, color) => {
        if (pts.length < 2) return;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        for (let i = 1; i < pts.length; i++) {
          const p0 = pts[i - 1];
          const p1 = pts[i];
          const alpha = (i / pts.length) * 0.55;
          const width = 0.5 + (i / pts.length) * 1.8;
          ctx.strokeStyle = color.replace("ALPHA", alpha.toFixed(3));
          ctx.lineWidth = width;
          ctx.beginPath();
          ctx.moveTo(p0.x, p0.y);
          ctx.lineTo(p1.x, p1.y);
          ctx.stroke();
        }
      };

      drawStroke(left.points, "rgba(26,26,24,ALPHA)");
      drawStroke(right.points, "rgba(200,62,43,ALPHA)");
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className={`dual-line-canvas ${className}`} aria-hidden="true" />;
}
